import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import UseSvg from "@SharedVueComponents/Svg/UseSvg.vue";
import { pushEvent } from "@/services/dataLayerLogin";
import { store } from "@/state/store";
import { authenticate } from "@/services/ocmAuthentication";
import { loadScript } from "@/services/utils";
import { useI18n } from "vue-i18n";
import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants.js";
import { onBeforeUnmount, onMounted, ref } from "vue";

type FacebookData = {
	accessToken: string;
	facebookId: string;
	context: string;
	rememberMe?: boolean;
	formId?: string;
};

const facebookSdkUrl = "https://connect.facebook.net/de_DE/sdk.js";

export default /*#__PURE__*/_defineComponent({
  __name: 'FacebookLogin',
  setup(__props) {

const contextPath = window.ocm.config.encodedContextPath;
const facebookInThirdPartyAppAuthAction = `${contextPath}/rest/authapp/facebook/`;

const { t } = useI18n();
const messangerId = ref<number | null>(null);
const initialized = ref(false);

onMounted(() => {
	if (store.isModal) {
		messangerId.value = OcmMessenger.subscribe(messengerPublicationTypes.REQUEST_LOGIN, loadSDK);
	} else {
		loadSDK();
	}
});

onBeforeUnmount(() => {
	if (store.isModal) messangerId.value && OcmMessenger.unsubscribe(messangerId.value);
});

async function loadSDK() {
	if (initialized.value) return;
	return loadScript(facebookSdkUrl)
		.then(() => {
			FB.init({
				appId: window.ocm.config.facebookAppId,
				version: window.ocm.config.facebookAppVersion,
				xfbml: true,
				cookie: true,
			});
			FB.getLoginStatus(() => null);
			initialized.value = true;
		})
		.catch(() => {
			pushEvent("facebook_load_error");
		});
}

function startFacebookLogin() {
	// todo todo TUB-18950: remove suffix after AB-Test
	const suffix = store.abTestIsTestPage && !store.abTestFirstStep ? "2" : "";
	pushEvent(store.isModal ? "FacebookBtnClick" : `FacebookButton${suffix}`);
	store.globalErrorMessages = [];
	if (initialized.value) {
		facebookAuthentication();
	} else {
		loadSDK().then(facebookAuthentication);
	}
}

function facebookAuthentication() {
	if (typeof FB !== "object") {
		const errorMessage = t(`errorMessages.httpError`);
		store.isSocialLogin = true;
		store.globalErrorMessages = [errorMessage];
		pushEvent("facebook_click_error", errorMessage);
		return false;
	}

	const scope = "email";
	FB.getLoginStatus((response) => {
		if (response.status === "connected") {
			const accessToken = response.authResponse.accessToken;
			const authorationData = createfacebookAutorizationData(accessToken, response.authResponse.userID, "authenticate");
			startOcmLogin(authorationData);
		} else if (response.status === "unknown") {
			_facebookLogin({ auth_type: "rerequest", scope, return_scopes: true }, "authenticate");
		} else {
			_facebookLogin({ scope, return_scopes: true }, "authenticate");
		}
	}, false);
}

function _facebookLogin(fbParameters: fb.LoginOptions, context: string) {
	if (_isInThirdPartyApp()) {
		const authenticationUrl = _buildFacebookThirdPartyAppAuthenticationUrl(
			facebookInThirdPartyAppAuthAction,
			fbParameters
		);
		window.location.replace(authenticationUrl);
	} else {
		FB.login((loginResponse) => {
			const response = loginResponse;
			if (response.authResponse) {
				const authorationData: FacebookData = createfacebookAutorizationData(
					response.authResponse.accessToken,
					response.authResponse.userID,
					context
				);
				startOcmLogin(authorationData);
			}
		}, fbParameters);
	}
}

function createfacebookAutorizationData(accessToken: string, facebookId: string, context: string) {
	return {
		accessToken,
		facebookId,
		context,
	} satisfies FacebookData;
}

// Handle 3rd Party App
function _isInThirdPartyApp() {
	const useragent = navigator.userAgent;
	const webView = "WebView";
	const iOS = "(iPhone|iPod|iPad)(?!.*Safari/)";
	const android = "Android.*(wv|.0.0.0)";
	const rules = [webView, iOS, android];
	const regex = new RegExp(`(${rules.join("|")})`, "ig");
	return useragent.match(regex);
}

function _buildFacebookThirdPartyAppAuthenticationUrl(callbackUrl: string, fbParameters: fb.LoginOptions) {
	const urlPath = `https://www.facebook.com/${window.ocm.config.facebookAppVersion}/dialog/oauth`;
	const url = new URL(urlPath);
	url.searchParams.append(`client_id`, window.ocm.config.facebookAppId);
	url.searchParams.append(`redirect_uri`, `${window.location.origin}${callbackUrl}`);

	Object.entries(fbParameters).forEach(([key, value]) => {
		url.searchParams.append(key, value as string);
	});

	return url.toString();
}

// ocm Login
async function startOcmLogin(authorationData: FacebookData) {
	authorationData.formId = "#facebook-login-form";
	const ok = await authenticate(authorationData, "facebook");

	if (ok) return;
	FB.logout((response) => {
		console.log(
			`facebook logout to reset the connection state done. At the next login the authentication state will be 'unknown' -> reauthentication is possible`,
			response
		);
	});
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("button", {
    type: "button",
    class: "flex-center m-2 flex rounded-3xl border border-solid border-[#dadce0] bg-white p-2.5 hover:border-[#d2e3fc] hover:bg-[rgba(66,133,244,.04)]",
    onClick: startFacebookLogin
  }, [
    _createVNode(UseSvg, {
      "svg-id": "social-facebook",
      class: "svg-icon flex-icon social-icon pointer-events-none h-[18px] w-[18px]"
    })
  ]))
}
}

})